/**
 * angular-admin-oskey-io
 *
 * @license
 * Copyright (c) 2021-2025, OSKEY SAS. All rights reserved.
 *
 * The source code is protected under international copyright law.
 * All rights reserved and protected by the copyright holders.
 *
 * The source code is confidential and only available to authorized individuals with the permission of the copyright holders.
 * If you encounter this source code and do not have permission, please contact the copyright holders and delete this file.
 */

import { Component, HostListener, Signal, effect, inject } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { NgClass, ViewportScroller } from '@angular/common';
import { OSKCookieConsentService } from './components/cookie-consent/services/cookie_consent.service';
import { OSKHeaderComponent } from './components/header/header.component';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { OSKAuthService } from './features/authentication/services/auth.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OSKCreateUserDTO, OSKCurrentUser } from '@oskey/core/types';
import { OSKTranslatePipe } from '@oskey/translate';
import { OSKSidemenuComponent } from './features/portals/sidemenu/sidemenu.component';
import { OSKCurrentUserToken } from './core';
import { environment } from '../environments/environment';
import {
  OSKAppVersionComponent,
  OSKButtonComponent,
  OSKCardComponent,
  OSKFormFieldComponent,
  OSKInputComponent,
  OSKSpinnerComponent,
  OSKToastContainerComponent
} from '@oskey/ui';

@Component({
  selector: 'osk-root',
  standalone: true,
  imports: [
    RouterOutlet,
    OSKHeaderComponent,
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    OSKTranslatePipe,
    OSKSidemenuComponent,
    OSKButtonComponent,
    OSKCardComponent,
    OSKFormFieldComponent,
    OSKInputComponent,
    OSKSpinnerComponent,
    OSKToastContainerComponent,
    OSKAppVersionComponent
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class OSKAppComponent {
  private readonly currentUser: Signal<OSKCurrentUser> = inject(OSKCurrentUserToken);
  protected readonly authService: OSKAuthService = inject(OSKAuthService);
  private readonly router: Router = inject(Router);

  private scroll: ViewportScroller = inject(ViewportScroller);
  private cookieConsentService: OSKCookieConsentService = inject(OSKCookieConsentService);

  protected readonly appVersion: string = environment.version;
  protected windowScrolled: boolean = false;
  protected showProfileInfoForm: boolean = false;

  protected profileForm = new FormGroup({
    firstName: new FormControl('', Validators.required),
    lastName: new FormControl('', Validators.required)
  });

  private user?: OSKCurrentUser;

  protected currentUrl: string = '';
  protected isInPlatformPortal: boolean = false;
  protected showNavbar: boolean = true;
  constructor() {
    this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (
          this.user?.isOskVerified === false &&
          !event.url.startsWith('/auth') &&
          !event.url.startsWith('/osk-ui')
        ) {
          this.showProfileInfoForm = true;
        }
      }
      if (event instanceof NavigationEnd) {
        this.currentUrl = event.url;
        this.isInPlatformPortal = this.currentUrl.startsWith('/admin');
        this.showNavbar = !(
          this.currentUrl.startsWith('/auth') ||
          this.currentUrl.startsWith('/osk-ui') ||
          !this.user
        );
      }
    });
    effect(() => {
      const user = this.currentUser();
      this.user = user;
      if (user.isOskVerified === false) {
        if (!this.router.url.startsWith('/auth') && !this.router.url.startsWith('/osk-ui')) {
          this.showProfileInfoForm = true;
        }
        this.showNavbar = !(
          this.router.url.startsWith('/auth') ||
          this.router.url.startsWith('/osk-ui') ||
          !user
        );
      }
    });
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    if (window.scrollY || document.documentElement.scrollTop || document.body.scrollTop > 100) {
      this.windowScrolled = true;
    } else if (
      (this.windowScrolled && window.scrollY) ||
      document.documentElement.scrollTop ||
      document.body.scrollTop < 10
    ) {
      this.windowScrolled = false;
    }
  }

  scrollToTop(): void {
    this.scroll.scrollToPosition([0, 0]);
  }

  showCookieConsentBanner(): void {
    this.cookieConsentService.showBanner();
  }

  createUser(): void {
    if (!this.user?.firebaseUser) {
      return;
    }

    if (!this.user.firebaseUser.email) {
      return;
    }

    if (!this.profileForm.controls.firstName.value || !this.profileForm.controls.lastName.value) {
      return;
    }

    const userDTO: OSKCreateUserDTO = {
      userId: this.user.firebaseUser.uid,
      email: this.user.firebaseUser.email,
      publicProfile: {
        userId: this.user.firebaseUser.uid,
        firstName: this.profileForm.controls.firstName.value,
        lastName: this.profileForm.controls.lastName.value
      }
    };

    this.authService.createUser(userDTO).then(() => {
      this.authService
        .updateProfile({
          displayName: `${userDTO.publicProfile.firstName} ${userDTO.publicProfile.lastName}`
        })
        .then(() => {
          this.showProfileInfoForm = false;
          setTimeout(() => {
            this.router.navigateByUrl('/admin');
          }, 500);
        });
    });
  }
}
